import { useCallback, useEffect, useRef, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import type {
  AnalyticsDateRange,
  AnalyticsFilters,
} from "@/features/analytics/types";
import { createDeviceListRequest } from "../utils";
import type { Coverage } from "@adlywise/shared";
import type { DeviceListResponse, DeviceListRow } from "../types";
import { deviceListOf } from "../coverage";

type UseDeviceListParams = {
  selectedRange: AnalyticsDateRange;
  appliedFilters: AnalyticsFilters;
  page: number;
  pageSize: number;
};

type UseDeviceListResult = {
  rows: DeviceListRow[];
  coverage: Coverage | null;
  isLoading: boolean;
  errorMessage: string;
  refresh: () => void;
};

export const useDeviceList = ({
  selectedRange,
  appliedFilters,
  page,
  pageSize,
}: UseDeviceListParams): UseDeviceListResult => {
  const { performRequest } = useApp();
  const [rows, setRows] = useState<DeviceListRow[]>([]);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(() => {
    setRefreshTick((tick) => tick + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const payload = createDeviceListRequest(
      selectedRange,
      appliedFilters,
      page,
      pageSize,
    );

    setIsLoading(true);
    setErrorMessage("");
    // A previous answer's coverage never stands for this one.
    setCoverage(null);

    performRequest<DeviceListResponse | null>("/devices", {
      method: "POST",
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        const next = deviceListOf(response);
        setRows(next.rows);
        setCoverage(next.coverage);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setRows([]);
        setCoverage(null);
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to load devices",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [
    performRequest,
    selectedRange,
    appliedFilters,
    page,
    pageSize,
    refreshTick,
  ]);

  return { rows, coverage, isLoading, errorMessage, refresh };
};
